<template>
    <Menu ref="contextMenu" :model="items" popup class="table-allocation-button-context-menu">
        <template #item="{ item, label, props }">
            <!-- custom action -->
            <div v-if="item.adjustEndUsersCountOption" class="adjust-users-count" @click.stop>
                <i class="p-menuitem-icon fal fa-user"></i>
                <EndUsersCountSelector @updated="onAdjustEndUsersCountSelected" />
            </div>

            <!-- default rendering -->
            <a
                v-if="!item.custom"
                v-bind="props.action"
                class="p-menuitem-link"
                :class="{ 'p-disabled': item.disabled }"
            >
                <i class="p-menuitem-icon" :class="item.icon"></i>
                <span class="p-menuitem-text">{{ label }}</span>
            </a>
        </template>
    </Menu>
</template>

<script setup lang="ts">
    import { useBillStore } from "@/data/stores/bill";
    import { useTableAllocationStore } from "@/data/stores/tableAllocation";
    import { replaceString } from "@/data/util/String";
    import { translations } from "@/shared/localization/translations";
    import { computed, ref } from "vue";

    import EndUsersCountSelector from "@/components/tableAllocation/EndUsersCountSelector.vue";
    import Menu from "primevue/menu";
    import type { MenuItem } from "primevue/menuitem";

    const bill = useBillStore();
    const allocation = useTableAllocationStore();

    const props = defineProps<{
        showTransferOption?: boolean;
    }>();

    const emit = defineEmits<{
        onRenameSelected: [];
        onTransferAllSelected: [];
        onTransferSelectedItemsSelected: [];
        onCheckoutSelected: [];
        onPrintSelected: [];
        onAdjustEndUsersCountSelected: [count: number];
    }>();

    const itemsToTransferCount = computed(() => bill.totalItemsSelected);
    const isTransferAllAvailable = computed(() => bill.isOpenBill && bill.totalItemsSelected == 0);

    const contextMenu = ref<InstanceType<typeof Menu> | null>(null);

    const onAdjustEndUsersCountSelected = (count: number) => {
        emit("onAdjustEndUsersCountSelected", count);
        close();
    };

    const items = computed(() => {
        const options: MenuItem[] = [];

        // Adjust end users count option
        options.push({
            custom: true,
            adjustEndUsersCountOption: true,
            disabled: bill.currentBill == null,
        });

        // Rename
        options.push({
            label: translations.changeAllocationDetailsModal.rename,
            disabled: bill.currentBill == null,
            icon: "fa-light fa-pen",
            command: () => emit("onRenameSelected"),
        });

        // Print
        options.push({
            label: translations.changeAllocationDetailsModal.print,
            disabled: bill.currentBill == null,
            icon: "fa-light fa-print",
            command: () => emit("onPrintSelected"),
        });

        if (props.showTransferOption) {
            // Transfer selected items
            if (itemsToTransferCount.value > 0) {
                options.push({
                    label: replaceString(
                        translations.changeAllocationDetailsModal.transferSelectedBillItems,
                        itemsToTransferCount.value.toString()
                    ),
                    disabled: false,
                    icon: "fa-light fa-right-to-bracket",
                    command: () => emit("onTransferSelectedItemsSelected"),
                });
            }
            // Transfer all items
            else {
                options.push({
                    label: translations.changeAllocationDetailsModal.transferBill,
                    disabled: !isTransferAllAvailable.value,
                    icon: "fa-light fa-right-to-bracket",
                    command: () => emit("onTransferAllSelected"),
                });
            }
        }

        // Checkout
        options.push({
            label: translations.changeAllocationDetailsModal.removeEndUsers,
            disabled: !allocation.hasAnyGuests,
            icon: "fa-light fa-user-xmark",
            command: () => emit("onCheckoutSelected"),
        });

        return options;
    });

    function show(e: Event) {
        contextMenu.value?.show(e);
    }
    function close() {
        contextMenu.value?.hide();
    }

    // define exposed
    defineExpose({
        show,
        close,
    });
</script>

<style lang="scss">
    .table-allocation-button-context-menu.p-menu {
        font-size: 13px;
        min-width: 12.5em;
        padding: 0.25em 0;

        ul.p-menu-list {
            padding: 0 0.25em;
            margin-bottom: 0;
        }

        .p-menuitem > .p-menuitem-content {
            width: 100%;
            border-radius: 4px;
            transition:
                background-color 0.2s,
                color 0.2s;

            .p-menuitem-link {
                cursor: pointer;
                display: flex;
                align-items: center;
                text-decoration: none;
                overflow: hidden;
                position: relative;
                padding: 0.75em 1em;
            }

            .p-menuitem-icon {
                margin-right: 0.5em;
                font-size: 1.25em;
            }
        }

        .adjust-users-count {
            padding: 0.25em 0.5em;
            padding-left: 1em;
            display: flex;
            align-items: center;
        }
    }
</style>
